/**
 * Plain-text job ticket for an RFQ. Used for "copy summary" in the
 * browser, the CRM comment, and future e-mail notifications.
 * Staff-facing, so it is always Ukrainian.
 */
import type { Configuration, GiftSetConfiguration, ProductConfiguration, RfqPayload } from './rfq';

export const PRODUCT_LABEL_UK: Record<string, string> = {
  candle: 'Свічка',
  diffuser: 'Дифузор',
  bottle: 'Пляшка / банка',
  cosmetic: 'Косметична тара',
  drinkware: 'Термос / пляшка для напоїв',
  mug: 'Керамічне горнятко (архів)',
  gift: 'Корпоративний набір',
};

export const MATERIAL_LABEL_UK: Record<string, string> = {
  clear: 'Прозоре скло',
  amber: 'Бурштинове скло',
  black: 'Чорне скло',
  white: 'Біле (опал) скло',
  frosted: 'Матове скло',
  green: 'Зелене скло',
  cobalt: 'Кобальтове скло',
  ceramic_white: 'Біла кераміка',
  ceramic_black: 'Чорна кераміка',
  ceramic_cream: 'Кремова кераміка',
  ceramic_sage: 'Шавлієва кераміка',
  steel: 'Нержавіюча сталь (браш)',
  powder_white: 'Порошкова фарба, біла',
  powder_black: 'Порошкова фарба, чорна',
  powder_sage: 'Порошкова фарба, шавлієва',
  powder_navy: 'Порошкова фарба, темно-синя',
};
/** @deprecated */
export const GLASS_LABEL_UK = MATERIAL_LABEL_UK;

export const INK_LABEL_UK: Record<string, string> = {
  cmyk: 'CMYK',
  cmyk_white: 'CMYK + White',
  cmyk_white_varnish: 'CMYK + White + Varnish',
};

export const COVERAGE_LABEL_UK: Record<string, string> = {
  single_logo: 'Одне лого',
  partial: 'Часткове декорування',
  full_wrap: 'Повне обгортання 360°',
};

export const PACKAGING_LABEL_UK: Record<string, string> = {
  kraft: 'Крафтова коробка з наповнювачем',
  black_box: 'Чорна подарункова коробка з ложементом',
  none: 'Без упаковки',
};

const mm = (n: number) => `${Math.round(n * 10) / 10} мм`;

export type LineId =
  | 'product'
  | 'container'
  | 'glass'
  | 'band'
  | 'ink'
  | 'coverage'
  | 'artwork'
  | 'resolution'
  | 'sha'
  | 'fileId'
  | 'notes'
  | 'placement'
  | 'coverageRatio'
  | 'mockup'
  | 'items'
  | 'personalization'
  | 'packaging'
  | 'logoScale';
export type Line = [id: LineId, label: string, value: string];

function artworkLines(a: ProductConfiguration['artwork'], designHelp: boolean, label = 'Макет'): Line[] {
  const lines: Line[] = [];
  if (a) {
    lines.push(['artwork', label, `${a.fileName} · ${a.sourceFormat.toUpperCase()} · ${(a.sizeBytes / 1024 / 1024).toFixed(2)} МБ`]);
    lines.push([
      'resolution',
      'Роздільність',
      a.kind === 'vector' ? 'вектор' : `${a.pixelWidth}×${a.pixelHeight} px${a.effectiveDpi ? ` · ~${a.effectiveDpi} dpi у розмірі друку` : ''}`,
    ]);
    lines.push(['sha', 'SHA-256', a.sha256.slice(0, 16) + '…']);
    if (a.artworkId) lines.push(['fileId', 'ID файлу', a.artworkId]);
    if (a.warnings.length) lines.push(['notes', 'Зауваження', a.warnings.join('; ')]);
  } else {
    lines.push(['artwork', label, designHelp ? 'Немає — потрібна розробка дизайну RDK' : 'Не завантажено']);
  }
  return lines;
}

function productLines(c: ProductConfiguration): Line[] {
  const lines: Line[] = [
    ['product', 'Продукт', PRODUCT_LABEL_UK[c.product] ?? c.product],
    ['container', 'Тара', `${c.containerLabel} · Ø ${mm(c.diameterMm)} × H ${mm(c.heightMm)} · ${c.volumeMl} мл${c.closure ? ` · ${c.closure}` : ''}`],
    ['glass', 'Матеріал', MATERIAL_LABEL_UK[c.material] ?? c.material],
    ['band', 'Зона друку', `${mm(c.printBand.circumferenceMm)} × ${mm(c.printBand.heightMm)} (розгортка)`],
    ['ink', 'Фарби', INK_LABEL_UK[c.ink] ?? c.ink],
    ['coverage', 'Декорування', COVERAGE_LABEL_UK[c.coverage] ?? c.coverage],
    ...artworkLines(c.artwork, c.designHelpRequested),
  ];
  if (c.placement) {
    const p = c.placement;
    lines.push([
      'placement',
      'Розміщення',
      `${mm(p.widthMm)} × ${mm(p.heightMm)}, центр U ${mm(p.uMm)} / V ${mm(p.vMm)}, поворот ${Math.round(p.rotationDeg)}°`,
    ]);
  }
  if (c.coverageRatio != null) lines.push(['coverageRatio', 'Покриття зони', `${Math.round(c.coverageRatio * 100)}%`]);
  if (c.mockupId) lines.push(['mockup', 'ID мокапа', c.mockupId]);
  return lines;
}

function giftLines(g: GiftSetConfiguration): Line[] {
  const items = g.items
    .map((i) => `${PRODUCT_LABEL_UK[i.product]} ${i.containerLabel} (${MATERIAL_LABEL_UK[i.material] ?? i.material})${i.qtyPerSet > 1 ? ` × ${i.qtyPerSet}` : ''}`)
    .join('; ');
  const lines: Line[] = [
    ['product', 'Продукт', PRODUCT_LABEL_UK.gift],
    ['items', 'Склад набору', items],
    ['packaging', 'Упаковка', PACKAGING_LABEL_UK[g.packaging] ?? g.packaging],
    ['ink', 'Фарби', INK_LABEL_UK[g.ink] ?? g.ink],
    ...artworkLines(g.logo, g.designHelpRequested, 'Логотип'),
    ['logoScale', 'Розмір лого', `${Math.round(g.logoScale * 100)}% висоти зони друку`],
    [
      'personalization',
      'Персоналізація',
      g.personalization.mode === 'names' ? `Імена: ${g.personalization.names.length} шт (${g.personalization.names.slice(0, 3).join(', ')}${g.personalization.names.length > 3 ? '…' : ''})` : 'Без персоналізації',
    ],
  ];
  if (g.mockupId) lines.push(['mockup', 'ID мокапа', g.mockupId]);
  return lines;
}

export function configurationLines(c: Configuration): Line[] {
  return c.product === 'gift' ? giftLines(c) : productLines(c);
}

export function rfqToPlainText(r: RfqPayload & { rfqNumber?: string }): string {
  const out: string[] = [];
  out.push(`RDK · Запит комерційної пропозиції${r.rfqNumber ? ` ${r.rfqNumber}` : ''}`);
  out.push('');
  out.push(`Компанія: ${r.contact.company}`);
  out.push(`Контактна особа: ${r.contact.contactPerson}`);
  out.push(`Email: ${r.contact.email}`);
  out.push(`Телефон: ${r.contact.phone}`);
  if (r.order) {
    const unit = r.configuration?.product === 'gift' ? 'наборів' : 'шт';
    out.push(`Тираж: ${r.order.quantity.toLocaleString('uk-UA')} ${unit} · дизайнів: ${r.order.skuCount}`);
    if (r.order.targetDate) out.push(`Бажана дата: ${r.order.targetDate}`);
  }
  if (r.productInterest) out.push(`Напрям: ${r.productInterest}`);
  if (r.configuration) {
    out.push('');
    for (const [, k, v] of configurationLines(r.configuration)) out.push(`${k}: ${v}`);
    if (r.configuration.product === 'gift' && r.configuration.personalization.mode === 'names') {
      out.push('');
      out.push('Список імен:');
      out.push(r.configuration.personalization.names.join('\n'));
    }
  }
  if (r.comments) {
    out.push('');
    out.push(`Коментар: ${r.comments}`);
  }
  return out.join('\n');
}
